package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.AppTooltipText
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.domain.model.RhVacancies
import br.com.vvrefeicoes.portal.domain.model.RhVacancy
import com.composables.icons.lucide.Building
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.UserPlus
import com.composables.icons.lucide.Users
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_col_function
import portalvv.shared.generated.resources.rh_col_requester
import portalvv.shared.generated.resources.rh_col_rmp
import portalvv.shared.generated.resources.rh_col_shift
import portalvv.shared.generated.resources.rh_col_status
import portalvv.shared.generated.resources.rh_col_unit
import portalvv.shared.generated.resources.rh_col_updated_at
import portalvv.shared.generated.resources.rh_vacancies_by_function
import portalvv.shared.generated.resources.rh_vacancies_by_status
import portalvv.shared.generated.resources.rh_vacancies_by_unit
import portalvv.shared.generated.resources.rh_vacancies_empty
import portalvv.shared.generated.resources.rh_vacancies_hint
import portalvv.shared.generated.resources.rh_vacancies_list
import portalvv.shared.generated.resources.rh_vacancies_top_function
import portalvv.shared.generated.resources.rh_vacancies_top_unit
import portalvv.shared.generated.resources.rh_vacancies_total

private val KPI_MIN_WIDTH = 240.dp
private val CARD_MIN_WIDTH = 360.dp
private const val TOP_LIMIT = 10
private const val VACANCIES_PAGE_SIZE = 20

private const val WEIGHT_RMP = 0.7f
private const val WEIGHT_STATUS = 1.4f
private const val WEIGHT_UNIT = 2f
private const val WEIGHT_FUNCTION = 2f
private const val WEIGHT_SHIFT = 1f
private const val WEIGHT_REQUESTER = 1.6f
private const val WEIGHT_UPDATED_AT = 1.3f

@Composable
fun RhVacanciesContent(vacancies: RhVacancies) {
    Text(
        text = stringResource(Res.string.rh_vacancies_hint),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
    if (vacancies.total == 0) {
        EmptyState(message = stringResource(Res.string.rh_vacancies_empty))
        return
    }
    val topUnit = vacancies.byUnit.firstOrNull()
    val topFunction = vacancies.byFunction.firstOrNull()
    RhGrid(
        minColumnWidth = KPI_MIN_WIDTH,
        maxColumns = 3,
        items =
            listOf(
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.UserPlus,
                        iconColor = AppPalette.Primary500,
                        iconContainerColor = AppPalette.Primary100,
                        label = stringResource(Res.string.rh_vacancies_total),
                        value = vacancies.total.toString(),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Building,
                        iconColor = AppPalette.Warning500,
                        iconContainerColor = AppPalette.Warning100,
                        label = stringResource(Res.string.rh_vacancies_top_unit),
                        value = topUnit?.label.orRhDash(),
                        hint = topUnit?.count?.toString(),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Users,
                        iconColor = AppPalette.Success500,
                        iconContainerColor = AppPalette.Success100,
                        label = stringResource(Res.string.rh_vacancies_top_function),
                        value = topFunction?.label.orRhDash(),
                        hint = topFunction?.count?.toString(),
                        modifier = modifier,
                    )
                },
            ),
    )
    RhGrid(
        minColumnWidth = CARD_MIN_WIDTH,
        maxColumns = 3,
        items =
            listOf(
                { modifier ->
                    RhDistributionCard(
                        title = stringResource(Res.string.rh_vacancies_by_status),
                        items = vacancies.byStatus,
                        color = AppPalette.Info500,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhDistributionCard(
                        title = stringResource(Res.string.rh_vacancies_by_unit),
                        items = vacancies.byUnit.take(TOP_LIMIT),
                        color = AppPalette.Warning500,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhDistributionCard(
                        title = stringResource(Res.string.rh_vacancies_by_function),
                        items = vacancies.byFunction.take(TOP_LIMIT),
                        color = AppPalette.Primary500,
                        modifier = modifier,
                    )
                },
            ),
    )
    RhVacanciesTable(vacancies.vacancies)
}

@Composable
private fun RhVacanciesTable(vacancies: List<RhVacancy>) {
    var page by remember(vacancies) { mutableStateOf(0) }
    val pageCount = (vacancies.size + VACANCIES_PAGE_SIZE - 1) / VACANCIES_PAGE_SIZE
    val visible = vacancies.drop(page * VACANCIES_PAGE_SIZE).take(VACANCIES_PAGE_SIZE)

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Text(
                text = stringResource(Res.string.rh_vacancies_list),
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.padding(AppSpacing.space16),
            )
            RhVacancyRow {
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_rmp), WEIGHT_RMP)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_status), WEIGHT_STATUS)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_unit), WEIGHT_UNIT)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_function), WEIGHT_FUNCTION)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_shift), WEIGHT_SHIFT)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_requester), WEIGHT_REQUESTER)
                RhVacancyHeaderCell(stringResource(Res.string.rh_col_updated_at), WEIGHT_UPDATED_AT)
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            visible.forEach { vacancy ->
                RhVacancyRow {
                    RhVacancyCell("#${vacancy.rmpId}", WEIGHT_RMP)
                    Box(modifier = Modifier.weight(WEIGHT_STATUS)) {
                        AppBadge(
                            text = vacancy.status.orRhDash(),
                            containerColor = AppPalette.Neutral200,
                            contentColor = AppPalette.Neutral900,
                        )
                    }
                    RhVacancyCell(vacancy.unitName.orRhDash(), WEIGHT_UNIT)
                    RhVacancyCell(vacancy.functionName.orRhDash(), WEIGHT_FUNCTION)
                    RhVacancyCell(vacancy.shift.orRhDash(), WEIGHT_SHIFT)
                    RhVacancyCell(vacancy.requesterName.orRhDash(), WEIGHT_REQUESTER)
                    RhVacancyCell(formatIsoDateTime(vacancy.updatedAt).orRhDash(), WEIGHT_UPDATED_AT)
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            }
            AppPagination(
                page = page,
                pageCount = pageCount,
                onPageChange = { page = it.coerceIn(0, (pageCount - 1).coerceAtLeast(0)) },
                modifier = Modifier.align(Alignment.End).padding(AppSpacing.space8),
            )
        }
    }
}

@Composable
private fun RhVacancyRow(content: @Composable RowScope.() -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        content = content,
    )
}

@Composable
private fun RowScope.RhVacancyHeaderCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun RowScope.RhVacancyCell(
    text: String,
    weight: Float,
) {
    AppTooltipText(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        modifier = Modifier.weight(weight),
    )
}
